import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MdAdd, MdEdit, MdDelete, MdSearch } from 'react-icons/md';
import { Modal } from '../../../components/shared/Modal';
import { useToast } from '../../../components/shared/Toast';
import { authenticatedFetch } from '../../../lib/api';
import { TerminalForm } from './TerminalForm';
import { ClearFiltersButton, TableBodySkeleton, erpPageBtnPrimaryClass } from '@euroasiann/shared/ui';
import {
    pmTableWrapClass,
    pmThClass,
    pmTdClass,
    pmTrClass,
    pmIconBtnClass,
    pmDangerIconBtnClass,
    pmStatusClass,
} from '../port-management.ui';

interface Terminal {
    _id: string;
    name: string;
    code: string;
    portId: {
        _id: string;
        name: string;
        code: string;
    };
    allowedVesselTypes: string[];
    operatingHours: string;
    status: string;
}

export function TerminalList() {
    const [search, setSearch] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedTerminal, setSelectedTerminal] = useState<Terminal | null>(null);
    const { success, error } = useToast();
    const queryClient = useQueryClient();

    const { data: terminals = [], isLoading } = useQuery({
        queryKey: ['terminals'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/terminals');
            const data = await res.json();
            return data.data || [];
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const res = await authenticatedFetch(`/api/v1/terminals/${id}`, {
                method: 'DELETE',
            });
            if (!res.ok) throw new Error('Failed to delete terminal');
        },
        onSuccess: () => {
            success('Terminal deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['terminals'] });
        },
        onError: (err: any) => {
            error(err.message);
        },
    });

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this terminal?')) {
            deleteMutation.mutate(id);
        }
    };

    const searchLower = search.toLowerCase();
    const filteredTerminals = terminals.filter((t: Terminal) => {
        const name = (t.name ?? '').toLowerCase();
        const code = (t.code ?? '').toLowerCase();
        const portName = (t.portId?.name ?? '').toLowerCase();
        return name.includes(searchLower) || code.includes(searchLower) || portName.includes(searchLower);
    });

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="relative max-w-xl flex-1">
                        <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                        <input
                            type="search"
                            placeholder="Search terminals"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="h-10 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-0 pl-9 pr-3 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc] focus:outline-none focus:ring-1 focus:ring-[#0088cc]"
                        />
                    </div>
                    <ClearFiltersButton visible={!!search.trim()} onClick={() => setSearch('')} variant="toolbar" />
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setSelectedTerminal(null);
                        setIsFormOpen(true);
                    }}
                    className={erpPageBtnPrimaryClass}
                >
                    <MdAdd className="h-5 w-5" />
                    Add terminal
                </button>
            </div>

            <div className={pmTableWrapClass}>
                <table className="w-full min-w-[720px] border-collapse">
                    <thead>
                        <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
                            {['Terminal', 'Port', 'Operations', 'Status', 'Actions'].map((h) => (
                                <th key={h} className={pmThClass}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <TableBodySkeleton rows={6} cols={5} />
                        ) : filteredTerminals.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-12 text-center text-sm text-[hsl(var(--muted-foreground))]">
                                    No terminals found
                                </td>
                            </tr>
                        ) : filteredTerminals.map((t: Terminal) => (
                            <tr key={t._id} className={pmTrClass}>
                                <td className={pmTdClass}>
                                    <div className="font-medium">{t.name}</div>
                                    <div className="mt-0.5 font-mono text-xs text-[#0088cc]">{t.code}</div>
                                </td>
                                <td className={pmTdClass}>
                                    <div>{t.portId?.name}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{t.portId?.code}</div>
                                </td>
                                <td className={pmTdClass}>
                                    <div className="flex flex-wrap gap-1">
                                        {t.allowedVesselTypes?.length
                                            ? t.allowedVesselTypes.map((vt) => (
                                                <span key={vt} className="rounded-md bg-[hsl(var(--muted))] px-2 py-0.5 text-xs">{vt}</span>
                                            ))
                                            : <span className="text-[hsl(var(--muted-foreground))]">All vessel types</span>}
                                    </div>
                                    {t.operatingHours && (
                                        <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{t.operatingHours}</div>
                                    )}
                                </td>
                                <td className={pmTdClass}>
                                    <span className={pmStatusClass(t.status === 'Active')}>{t.status}</span>
                                </td>
                                <td className={pmTdClass}>
                                    <div className="flex items-center gap-0.5">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedTerminal(t);
                                                setIsFormOpen(true);
                                            }}
                                            className={pmIconBtnClass}
                                        >
                                            <MdEdit className="h-4 w-4" />
                                        </button>
                                        <button type="button" onClick={() => handleDelete(t._id)} className={pmDangerIconBtnClass}>
                                            <MdDelete className="h-4 w-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal
                isOpen={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                title={selectedTerminal ? 'Edit terminal' : 'Add terminal'}
            >
                <TerminalForm
                    initialData={selectedTerminal}
                    onSuccess={() => {
                        setIsFormOpen(false);
                        queryClient.invalidateQueries({ queryKey: ['terminals'] });
                    }}
                    onCancel={() => setIsFormOpen(false)}
                />
            </Modal>
        </div>
    );
}
